/* eslint-disable @angular-eslint/use-component-view-encapsulation */
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { startWith, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { MatInput } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TransferType } from '@nimbus-assistant/enums/transfer-type';
import { TaskFlowParticipantType } from '@nimbus-assistant/models/task-types.model';
import { OutboundCallState } from '@nimbus-global/features/shared/models/api/outbound-call-state.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS, Icon } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import {
  UserAutoCompleteCallOptionType,
  UserAutoCompleteValueType,
} from '@nimbus-library/features/shared/services/user-search/models/user-auto-complete-value-type';
import { isValidPhoneNumber } from '@nimbus-library/features/shared/components/material/core';
import { IIdName } from '@nimbus-library/features/shared/models/interfaces';
import { PhoneNumberService } from '@nimbus-library/features/shared/services/helper/phone-number.service';
import { AssistantTasksService } from '../../services/assistant-tasks.service';
import { TaskTransferService } from '../../services/task-transfer.service';
import { TeamContactInfo, TeamInfo } from '../../models/team-info';
import { IAssistantUserAutoCompleteValueModel } from '../../interfaces/assistant-user-autocomplete-value-model';
import { AppRoutes } from '../../enums/app-routes';
import { TaskOutboundService } from '../../services/task-outbound.service';
import { AuthService } from '../../services/auth.service';
import { RoutingService } from '../../services/routing.service';
import { localStorageKeys, LocalStorageService } from '../../services/local-storage.service';
import { IAssistantUserAutocompleteOption } from '../../interfaces/assistant-user-autocomplete';
import { resolveOutboundService } from './outbound-service-selection.helper';
import { InputAssistantUserAutocompleteComponent } from './assistant-user-autocomplete/input-assistant-user-autocomplete.component';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-outbound',
  templateUrl: './outbound.component.html',
  styleUrls: ['./outbound.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutboundComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('userAutocomplete')
  public userAutocomplete: InputAssistantUserAutocompleteComponent;

  @ViewChild(MatInput, { static: true })
  public input: MatInput;

  /** Optional phone number to prefill on the dialer. Also accepted via `?phone=` query param. */
  @Input() public phoneNumber?: string;

  /** Optional inbound service id; the dialer prefers it for callback when the service allows outbound. */
  @Input() public inboundServiceId?: string;

  public ICONS = ICONS;
  public TranslationKeys = TranslationKeys;
  public readonly IconWeight = IconWeight;

  public isCallButtonEnabled = true;
  public isPhoneNumberDisabled = false;

  public title$ = new BehaviorSubject<string>('');

  public form!: UntypedFormGroup;
  public filteredOptions: IAssistantUserAutocompleteOption[] = [];

  public tenantId = '';
  public services: TeamInfo[] = [];

  public UserAutoCompleteCallOptionType = UserAutoCompleteCallOptionType;

  public get callText(): string {
    switch (this.transferService.transferType) {
      case TransferType.blindTransfer:
      case TransferType.safeTransfer:
        return TranslationKeys.AssistantOutboundTransfer;
      case TransferType.consultativeTransfer:
        return TranslationKeys.AssistantCallFooterConsultativeTransfer;
      default:
        return TranslationKeys.AssistantOutboundCall;
    }
  }

  public get callIcon(): Icon {
    switch (this.transferService.transferType) {
      case TransferType.blindTransfer:
        return ICONS.SB_DIRECT_TRANSFER;
      case TransferType.safeTransfer:
        return ICONS.SB_TRANSFER;
      case TransferType.consultativeTransfer:
        return ICONS.CONSULTATION_CALL;
      default:
        return ICONS.PHONE;
    }
  }

  public constructor(
    public outboundService: TaskOutboundService,
    public transferService: TaskTransferService,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private tasksService: AssistantTasksService,
    private routingService: RoutingService,
    private readonly localStorageService: LocalStorageService,
    private readonly phoneNumberService: PhoneNumberService,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    this.applyRouteParams();

    const defaultService = this.resolveDefaultService();

    const isTransfer = !!this.transferService.callTransfer$.value;

    this.title$.next(this.getTitle());

    this.form = this.formBuilder.group({
      teamId: [defaultService?.teamId || '', isTransfer ? [] : [Validators.required]],
      contactId: [null, Validators.required],
    });

    this.tenantId = this.authService.getTenantId();

    this.form.controls.teamId.valueChanges.pipe(untilDestroyed(this), startWith(defaultService?.teamId)).subscribe((teamId: string) => {
      this.onServiceSelected(teamId);
    });

    if (isTransfer) {
      const isPstnService = this.transferService.callTransfer$.value?.isPstnService;
      const caller = this.tasksService.connectedTaskSnapshot?.callFlow?.find((x) => x.participantType === TaskFlowParticipantType.Caller);
      const isPstnCall = caller?.serviceName && this.isPhoneNumber(caller?.serviceName);
      this.isPhoneNumberDisabled = !(isPstnCall || isPstnService);
    }
  }

  public ngAfterViewInit(): void {
    this.outboundService.clickToCallTarget$.pipe(untilDestroyed(this)).subscribe((x) => {
      this.userAutocomplete.control.setValue(x);
    });

    if (this.phoneNumber) {
      this.userAutocomplete.control.setValue(this.phoneNumber);
    }
  }

  public onCall(): void {
    this.isCallButtonEnabled = false;
    const selectedOption = this.form.value.contactId;
    if (!selectedOption?.value) {
      return;
    }

    (this.transferService.callTransfer$.value
      ? this.transferService.transferCall(this.transferService.callTransfer$.value, selectedOption)
      : this.outboundService.processCall(selectedOption, this.form.value.teamId)
    )
      .pipe(untilDestroyed(this), take(1))
      .subscribe({
        next: () => {
          this.isCallButtonEnabled = true;
          this.routingService.navigate(AppRoutes.call);
        },
        error: () => {
          this.isCallButtonEnabled = true;
          this.routingService.navigate(AppRoutes.call);
        },
      });
  }

  public onCancel(): void {
    this.outboundService.cleanup();
    this.transferService.cleanup();
    this.routingService.goToDefaultPage();
  }

  public onDialButtonClick(value: string): void {
    const currentValue = this.userAutocomplete.control.value || '';
    this.userAutocomplete.control.setValue(currentValue + value);
  }

  public onDialButtonLongPress(char: string): void {
    const val = this.userAutocomplete.control.value || '';
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.userAutocomplete.control.setValue(val.slice(0, -1) + char);
  }

  public onServiceSelected(teamId: string): void {
    const service = this.outboundService.services$.value?.find((x) => x.id === teamId);
    const contact = this.form.controls.contactId.value;
    const isPstnContact = (contact as IAssistantUserAutoCompleteValueModel)?.type === UserAutoCompleteValueType.PSTN;
    if (!service?.pstnNumber) {
      this.isPhoneNumberDisabled = true;
      if (isPstnContact) {
        this.form.controls.contactId.setValue(null);
        this.userAutocomplete.control.setValue(null);
      }
    } else {
      this.isPhoneNumberDisabled = false;
      const userAutoCompleteValue = this.userAutocomplete?.control?.value;

      if (isValidPhoneNumber(userAutoCompleteValue)) {
        this.userAutocomplete?.control?.setValue(userAutoCompleteValue);
      }
    }
  }

  public onTargetAutocompleteSelectionChange(event: IIdName | null): void {
    if (this.transferService.callTransfer$.value && event) {
      this.transferService.callTransfer$.value.transferTargetName = event.name;
    }
  }

  public ngOnDestroy(): void {
    this.outboundService.cleanup();
  }

  private applyRouteParams(): void {
    const snapshot = this.activatedRoute?.snapshot?.queryParamMap;
    if (!snapshot) {
      return;
    }
    if (!this.phoneNumber) {
      this.phoneNumber = snapshot.get('phone') || undefined;
    }
    if (!this.inboundServiceId) {
      this.inboundServiceId = snapshot.get('inboundServiceId') || undefined;
    }
  }

  private resolveDefaultService(): TeamContactInfo | undefined {
    const services = this.outboundService.services$.value ?? [];
    const inboundEnabled =
      !!this.inboundServiceId &&
      services.some(
        (s) => (s.id === this.inboundServiceId || s.teamId === this.inboundServiceId) && s.outboundCallState === OutboundCallState.Enabled,
      );
    const result = resolveOutboundService({
      services,
      inboundServiceId: this.inboundServiceId,
      inboundServiceAllowsOutbound: inboundEnabled,
      lastUsedOutboundServiceId: this.localStorageService.getData(localStorageKeys.lastUsedOutboundService),
    });
    if (result.kind !== 'resolved') {
      return undefined;
    }
    return services.find((s) => s.id === result.serviceId);
  }

  private getTitle(): string {
    switch (this.transferService.transferType) {
      case TransferType.blindTransfer:
        return TranslationKeys.AssistantOutboundBlindTransfer;
      case TransferType.safeTransfer:
        return TranslationKeys.AssistantOutboundSafeTransfer;
      case TransferType.consultativeTransfer:
        return TranslationKeys.AssistantOutboundConsultativeTransfer;
      default:
        return TranslationKeys.AssistantOutboundCallAs;
    }
  }

  private isPhoneNumber(author: string): boolean {
    const phoneInfo = this.phoneNumberService.normalizeNumber(author);
    return phoneInfo?.isValid;
  }
}
